// Snap's fixed layout (Josh 10-01: "let's just pin and auto layout the webxr windows if you are in snap mode because
// it gets too hard to move them around without a controller"). In Snap the camera faces one window at a time and the
// ‹ › / swipes step along the arc in its order (view.js `order()`: by angle), so here the windows simply get their
// places in a row by what they are: Home first, then each session window followed by the windows it opened (a
// stream, a pane in its own window, a sheet-like window anchored to it), then everything else in the order it came.
// Nothing is dragged or resized by hand in Snap (app.js ignores those drags); opening or closing a window lays the
// row out again. Look and VR keep free placement: a window's own pose is kept aside while Snap holds it and given
// back when Snap ends (`freeLayout`), so nothing placed by hand there is lost.
//
// One seam: `snapLayout(specs, { RADIUS, isLive }, gap)` while in Snap; `freeLayout(specs)` before the free arrangement.

/// The order Snap shows the windows in.
export function snapOrder(specs) {
  const home = specs.filter((w) => w.id === 'home')
  const sessions = specs.filter((w) => w.kind === 'session' || w.winKind === 'session')
  const placed = new Set([...home, ...sessions])
  const own = (s) => specs.filter((w) => !placed.has(w) && (w.ownerID === s.id || (w.forSession && w.forSession === s.sessionID) || (w.rule?.[0] === 'session' && s === sessions[sessions.length - 1])))
  const out = [...home]
  for (const s of sessions) {
    out.push(s)
    for (const w of own(s)) { out.push(w); placed.add(w) }
  }
  for (const w of specs) if (!placed.has(w) && !out.includes(w)) out.push(w)
  return out
}

/// Every window's place in Snap's row, left to right, level with the eye (only the windows that are open: `isLive`).
/// The free pose is kept aside first.
export function snapLayout(specs, { RADIUS, isLive = () => true }, gap = 0.18) {
  let along = 0
  for (const spec of snapOrder(specs.filter((w) => isLive(w.id) || w.stream))) {
    if (!spec.freePose) spec.freePose = { angle: spec.angle, lift: spec.lift, placed: !!spec.placed }
    const r = spec.radius ?? RADIUS
    // its widest shape (Snap reshapes a session window to the screen, never wider than the headset's), so a window
    // reshaped later never reaches into its neighbour's place
    const w = Math.max(spec.w || 0, spec.w0 || 0)
    const mid = along + w / 2
    spec.angle = -(mid / r) * (180 / Math.PI)   // a negative angle is to your right: the row runs left to right
    spec.lift = 0
    along = mid + w / 2 + gap
  }
}

/// Snap is over: each window gets back the pose it had (a window opened during Snap keeps no pose: it is arranged).
export function freeLayout(specs) {
  for (const spec of specs) {
    const p = spec.freePose
    if (!p) continue
    delete spec.freePose
    if (p.placed) { spec.angle = p.angle; spec.lift = p.lift; spec.placed = true }
  }
}
